import type { CSSProperties } from "react";
import {
  DenkerLogo,
  DenkerThinkingLogo,
  surfaceRoleAttributes,
} from "@/components/ui";
import { cn } from "@/lib/cn";
import "./agent-cursor.css";

type CursorStatusAppearance = "annotation" | "presence";
type PresenceTheme = "dark" | "light" | "clear";

interface AgentCursorProps {
  agentName: string;
  agentColor: string;
  toolCall?: string;
  content?: string;
  thinking?: boolean;
  error?: string;
  pointing?: boolean;
  statusAppearance?: CursorStatusAppearance;
  presenceTheme?: PresenceTheme;
  className?: string;
  "data-testid"?: string;
}

type CursorStatus =
  | { kind: "thinking" }
  | { kind: "tool"; text: string }
  | { kind: "reply"; text: string }
  | { kind: "error"; text: string }
  | null;

function colorWithAlpha(color: string, alpha: number): string {
  const hex = color.replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(hex)) return color;
  return `rgba(${Number.parseInt(hex.slice(0, 2), 16)}, ${Number.parseInt(hex.slice(2, 4), 16)}, ${Number.parseInt(hex.slice(4, 6), 16)}, ${alpha})`;
}

function lightenWithAlpha(
  color: string,
  amount: number,
  alpha: number,
): string {
  const hex = color.replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(hex)) return color;
  const channel = (start: number) => {
    const value = Number.parseInt(hex.slice(start, start + 2), 16);
    return Math.round(value + (255 - value) * amount);
  };
  return `rgba(${channel(0)}, ${channel(2)}, ${channel(4)}, ${alpha})`;
}

function cursorBubbleStyle(color: string): CSSProperties {
  return {
    background: [
      `radial-gradient(150% 130% at 50% 118%, ${colorWithAlpha(color, 0.5)} 0%, ${colorWithAlpha(color, 0.16)} 46%, transparent 70%)`,
      "linear-gradient(180deg, rgba(26,32,41,0.97), rgba(16,21,28,0.97))",
    ].join(", "),
    borderColor: "rgba(255,255,255,0.1)",
    boxShadow: [
      "inset 0 1px 0 rgba(255,255,255,0.1)",
      `inset 0 -1px 0 ${colorWithAlpha(color, 0.4)}`,
      "0 0 0 1px rgba(255,255,255,0.07)",
      "0 8px 24px -10px rgba(0,0,0,0.7)",
      `0 4px 18px -6px ${colorWithAlpha(color, 0.38)}`,
    ].join(", "),
  };
}

function WorkshopCursorArrow({
  color,
  testId,
}: {
  color: string;
  testId: string;
}) {
  return (
    <svg
      className="absolute left-0 top-0 overflow-visible"
      width="17"
      height="22"
      viewBox="0 0 17 22"
      fill="none"
      aria-hidden="true"
      data-cursor-mode="glass"
      data-testid={`${testId}-arrow`}
      style={{
        filter: `drop-shadow(0 0 5px ${colorWithAlpha(color, 0.44)}) drop-shadow(0 2px 3px rgba(0,0,0,0.55))`,
        transform: "scale(0.65)",
        transformOrigin: "top left",
      }}
    >
      <path
        d="M1.91 0.06Q3.08 0.11 3.56 0.4Q4.04 0.69 10.15 6.13Q16.26 11.57 16.52 11.92Q16.79 12.26 16.89 12.58Q17 12.89 17 13.32Q17 13.75 16.87 14.12Q16.73 14.49 16.5 14.78Q16.26 15.07 15.99 15.24Q15.73 15.41 15.54 15.47Q15.35 15.53 12.8 15.53Q10.25 15.53 9.8 15.61Q9.35 15.7 8.87 15.93Q8.39 16.16 7.84 16.67Q7.28 17.19 5.87 19.22Q4.46 21.26 4.25 21.46Q4.04 21.66 3.64 21.83Q3.24 22 2.87 22Q2.5 22 2.1 21.83Q1.7 21.66 1.38 21.28Q1.06 20.91 0.93 20.48Q0.8 20.05 0.42 11.09Q0.05 2.12 0.29 1.55Q0.53 0.97 0.88 0.66Q1.22 0.34 1.57 0.2Z"
        fill="rgba(20,26,34,0.96)"
        stroke={color}
        strokeWidth="1.1"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d="M3.3 2.9L13.7 12.6L10.8 12.65C9.42 12.72 8.1 13.4 7.26 14.5L4.1 18.72L3.3 2.9Z"
        fill={lightenWithAlpha(color, 0.68, 0.35)}
      />
    </svg>
  );
}

function WorkshopShimmerText({
  children,
  compact = false,
  testId,
}: {
  children: string;
  compact?: boolean;
  testId: string;
}) {
  return (
    <span
      className={cn(
        "denker-workshop-cursor-shimmer min-w-0 whitespace-pre-wrap break-words text-appkit-caption not-italic [overflow-wrap:anywhere]",
        compact ? "leading-4" : "leading-relaxed",
      )}
      data-testid={testId}
    >
      {children}
    </span>
  );
}

function AnnotationStatus({
  agentColor,
  status,
  testId,
}: {
  agentColor: string;
  status: Exclude<CursorStatus, null>;
  testId: string;
}) {
  return (
    <div
      className={cn(
        "denker-workshop-annotation-material flex w-max max-w-[min(340px,100%)] shrink-0 items-start",
        "whitespace-pre-wrap break-words rounded-[18px] border px-3 py-2 text-appkit-caption font-medium leading-snug text-white/90 [overflow-wrap:anywhere]",
      )}
      style={cursorBubbleStyle(agentColor)}
      data-cursor-tone={status.kind === "error" ? "error" : "normal"}
      data-testid={`${testId}-bubble`}
    >
      {status.kind === "thinking" ? (
        <WorkshopShimmerText testId={`${testId}-thinking`}>
          Thinking…
        </WorkshopShimmerText>
      ) : status.kind === "tool" ? (
        <WorkshopShimmerText testId={`${testId}-tool`}>
          {status.text}
        </WorkshopShimmerText>
      ) : (
        <span
          className={status.kind === "error" ? "text-danger" : undefined}
          data-testid={`${testId}-status`}
        >
          {status.text}
        </span>
      )}
    </div>
  );
}

function PresenceStatus({
  agentName,
  status,
  testId,
  theme,
}: {
  agentName: string;
  status: Exclude<CursorStatus, null>;
  testId: string;
  theme: PresenceTheme;
}) {
  const thinking = status.kind === "thinking";
  return (
    <div
      {...surfaceRoleAttributes("rail", {
        nativeLevel: "island",
        nativeGroup: "undocked-agent-status",
      })}
      className={cn(
        "denker-workshop-presence-material relative flex w-max max-w-[min(340px,100%)] shrink-0 overflow-hidden",
        thinking
          ? "h-9 min-w-9 max-w-9 items-center justify-center rounded-full p-0"
          : "min-h-9 min-w-0 items-center rounded-[18px] px-2.5 py-2",
      )}
      data-agent-status-kind={status.kind}
      data-cursor-tone={status.kind === "error" ? "error" : "normal"}
      data-preview-theme={theme}
      data-testid={`${testId}-bubble`}
      aria-label={
        thinking ? `${agentName} is thinking` : `${agentName}: ${status.text}`
      }
    >
      {thinking ? (
        <DenkerThinkingLogo
          className="h-4 w-4"
          height={13}
          testId={`${testId}-thinking`}
        />
      ) : (
        <div className="flex min-w-0 items-start gap-2">
          <span
            aria-hidden="true"
            className="grid h-4 w-4 shrink-0 place-items-center text-[var(--denker-workshop-presence-foreground)] opacity-70"
            data-testid={`${testId}-logo`}
          >
            <DenkerLogo variant="symbol" color="currentColor" height={13} />
          </span>
          {status.kind === "tool" ? (
            <WorkshopShimmerText compact testId={`${testId}-tool`}>
              {status.text}
            </WorkshopShimmerText>
          ) : (
            <span
              className={cn(
                "min-w-0 flex-1 whitespace-pre-wrap break-words text-appkit-caption font-normal leading-4 text-[var(--denker-workshop-presence-foreground)] [overflow-wrap:anywhere]",
                status.kind === "error" && "text-red-400",
              )}
              data-testid={`${testId}-status`}
            >
              {status.text}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export function AgentCursor({
  agentName,
  agentColor,
  toolCall,
  content,
  thinking,
  error,
  pointing = true,
  statusAppearance,
  presenceTheme = "dark",
  className,
  "data-testid": testId = "agent-cursor",
}: AgentCursorProps) {
  const status: CursorStatus = error
    ? { kind: "error", text: error }
    : toolCall
      ? { kind: "tool", text: toolCall }
      : content
        ? { kind: "reply", text: content }
        : thinking
          ? { kind: "thinking" }
          : null;
  const appearance = statusAppearance ?? "presence";
  return (
    <div
      className={cn(
        "pointer-events-none relative min-h-10 min-w-10 [--agent-cursor-scale:1]",
        className,
      )}
      data-status-appearance={status ? appearance : undefined}
      data-testid={testId}
    >
      {pointing ? (
        <WorkshopCursorArrow color={agentColor} testId={testId} />
      ) : null}
      {status ? (
        <div
          className={cn(
            "absolute flex max-w-full flex-col items-start gap-1 leading-none",
            pointing ? "left-3 top-3" : "left-0 top-0",
          )}
        >
          {appearance === "presence" ? (
            <PresenceStatus
              agentName={agentName}
              status={status}
              testId={testId}
              theme={presenceTheme}
            />
          ) : (
            <AnnotationStatus
              agentColor={agentColor}
              status={status}
              testId={testId}
            />
          )}
        </div>
      ) : null}
    </div>
  );
}

export type { AgentCursorProps, CursorStatusAppearance, PresenceTheme };
